import { useEffect, useState } from "react";
import { AlertTriangle, Pencil, Plus, Target, Trash2, X } from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";
import { notify } from "../lib/toast";

const EXPENSE_CATS = [
  "Ofis & Kira", "Personel", "Yazılım & Abonelik", "Vergi & Sigorta",
  "Pazarlama", "Kargo & Lojistik", "Diğer Gider",
];

function BudgetModal({ open, onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ category: EXPENSE_CATS[0], monthly_limit: "", type: "expense" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          category: initial.category,
          monthly_limit: String(initial.monthly_limit),
          type: initial.type || "expense",
        });
      } else {
        setForm({ category: EXPENSE_CATS[0], monthly_limit: "", type: "expense" });
      }
      setError("");
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    const limit = Number(form.monthly_limit);
    if (!limit || limit <= 0) return setError("Geçerli bir limit girin");
    setBusy(true);
    try {
      await onSave({ category: form.category, monthly_limit: limit, type: form.type });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="budget-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isEdit ? "DÜZENLE" : "YENİ BÜTÇE"}</span>
            <h2>{isEdit ? "Bütçeyi düzenle" : "Kategori bütçesi"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-budget-modal"><X size={18} /></button>
        </div>
        <label>
          Kategori
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            disabled={isEdit}
            data-testid="budget-category-select"
          >
            {EXPENSE_CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>
          Aylık limit (₺)
          <input
            type="number"
            value={form.monthly_limit}
            onChange={(e) => setForm({ ...form, monthly_limit: e.target.value })}
            placeholder="20000"
            data-testid="budget-limit-input"
          />
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-budget-button">
          <Plus size={17} /> {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Bütçeyi kaydet"}
        </button>
      </div>
    </div>
  );
}

export default function BudgetsPage() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/budgets/status");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalLimit = items.reduce((s, b) => s + b.monthly_limit, 0);
  const totalSpent = items.reduce((s, b) => s + b.spent, 0);

  const remove = async (b) => {
    if (!window.confirm(`${b.category} bütçesi silinsin mi?`)) return;
    try {
      await api.delete(`/budgets/${b.id}`);
      notify.success("Bütçe silindi");
      load();
    } catch {
      notify.error("Silme başarısız");
    }
  };

  const onSave = async (payload) => {
    if (editing) {
      await api.patch(`/budgets/${editing.id}`, payload);
      notify.success("Bütçe güncellendi");
    } else {
      await api.post("/budgets", payload);
      notify.success("Bütçe eklendi");
    }
    await load();
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">BÜTÇE</div>
          <h1>Bütçe & Kategori</h1>
          <p>Kategori bazlı aylık limitleri belirleyin; limit aşımlarında anlık bildirim alın.</p>
        </div>
        <div className="head-actions">
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-budget-button">
            <Plus size={17} /> Bütçe ekle
          </button>
        </div>
      </div>

      <div className="account-summary panel">
        <div>
          <span>Toplam limit</span>
          <strong>{money(totalLimit)}</strong>
        </div>
        <div>
          <span>Bu ay harcanan</span>
          <strong className="expense-text">{money(totalSpent)}</strong>
        </div>
        <div>
          <span>Kalan</span>
          <strong className={totalLimit - totalSpent >= 0 ? "income-text" : "expense-text"}>
            {money(totalLimit - totalSpent)}
          </strong>
        </div>
      </div>

      <div className="panel inner-page" data-testid="budgets-panel">
        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="empty-state" data-testid="budgets-empty">
            <Target size={30} />
            <h3>Bütçe yok</h3>
            <p>İlk kategori bütçenizi oluşturarak başlayın.</p>
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-budget-btn">
              <Plus size={14} /> Bütçe ekle
            </button>
          </div>
        ) : (
          <div className="budget-list">
            {items.map((b) => {
              const percent = Math.min(b.percent, 100);
              const state = b.over ? "over" : b.percent >= 80 ? "warn" : "ok";
              return (
                <div className={`budget-row ${state}`} key={b.id} data-testid={`budget-${b.id}`}>
                  <div className="budget-head">
                    <div className="bud-title">
                      <strong>{b.category}</strong>
                      {b.over ? <span className="over-chip"><AlertTriangle size={11} /> Aşıldı</span> : null}
                    </div>
                    <div className="bud-nums">
                      <strong>{money(b.spent)}</strong>
                      <span>/ {money(b.monthly_limit)}</span>
                    </div>
                    <div className="bud-actions">
                      <button
                        className="icon-btn ghost"
                        onClick={() => { setEditing(b); setModal(true); }}
                        data-testid={`edit-budget-${b.id}`}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        className="icon-btn ghost"
                        onClick={() => remove(b)}
                        data-testid={`delete-budget-${b.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="bud-track">
                    <div className={`bud-fill ${state}`} style={{ width: `${percent}%` }} />
                  </div>
                  <div className="bud-foot">
                    <span>{b.percent.toFixed(1)}% kullanıldı</span>
                    {b.over ? (
                      <span className="expense-text">{money(b.spent - b.monthly_limit)} aşım</span>
                    ) : (
                      <span className="income-text">{money(b.monthly_limit - b.spent)} kaldı</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BudgetModal open={modal} onClose={closeModal} onSave={onSave} initial={editing} />
    </>
  );
}
